import { useEffect, useState } from "react";
import type { Application, Endpoints } from "../api";
import { applicationTypeLabel } from "../presentation";
import { ApplicationStatusBadge } from "./ApplicationStatusBadge";
import { Button } from "./ui/Button";
import { CopyField } from "./ui/CopyField";
import { Icon } from "./ui/Icon";

type SdkPlatform = "typescript" | "kotlin" | "swift" | "flutter" | "java-server";

interface SdkExample {
  readonly label: string;
  readonly language: string;
  readonly install: string;
  readonly code: string;
  readonly note: string;
}

export function QuickstartView({ application, applications, endpoints, onSelect, onManageApplications }: {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly endpoints: Endpoints | null;
  readonly onSelect: (application: Application) => void;
  readonly onManageApplications: () => void;
}) {
  const [platform, setPlatform] = useState<SdkPlatform>(application?.appType === "native" ? "kotlin" : "typescript");
  useEffect(() => { setPlatform(application?.appType === "native" ? "kotlin" : "typescript"); }, [application?.appId, application?.appType]);

  if (application === null) {
    return <section className="empty-page"><strong>请先创建一个应用</strong><p>完成应用注册后，平台会生成对应的接入参数和多端示例代码。</p><Button variant="primary" onClick={onManageApplications}>前往应用中心</Button></section>;
  }
  if (endpoints === null) return <div className="management-state"><span className="loader" /><strong>正在加载接入参数</strong><p>正在读取当前应用的 API、WebSocket 和 SDK 配置…</p></div>;
  const platforms: readonly SdkPlatform[] = application.appType === "native"
    ? ["kotlin", "swift", "flutter", "java-server"]
    : ["typescript", "java-server"];
  const examples = sdkExamples(application, endpoints);
  const example = examples[platform] ?? examples[platforms[0] ?? "typescript"];
  if (example === undefined) return null;

  return <div className="quickstart-page">
    <section className="page-heading compact-heading">
      <div><p className="breadcrumb"><span>接入与安全</span><Icon name="chevron-right" size={14} />SDK 快速接入</p><h2>SDK 快速接入</h2><p>从业务登录、UserSig 签发到消息同步的最小可运行路径。</p></div>
      <label className="application-select"><span>当前应用</span><select value={application.appId} onChange={(event) => { const next = applications.find((item) => item.appId === event.currentTarget.value); if (next !== undefined) onSelect(next); }}>{applications.map((item) => <option key={item.appId} value={item.appId}>{item.name}</option>)}</select></label>
    </section>

    <section className="quickstart-summary"><div><strong>{application.name}</strong><span>{applicationTypeLabel(application.appType)} · {application.appKey}</span></div><ApplicationStatusBadge status={application.status} /></section>

    <div className="sdk-platform-tabs" role="tablist" aria-label="选择 SDK 平台">
      {platforms.map((value) => <button aria-selected={platform === value} className={platform === value ? "active" : ""} key={value} role="tab" type="button" onClick={() => { setPlatform(value); }}>{platformLabel(value)}</button>)}
    </div>

    <div className="quickstart-layout">
      <article className="surface-card onboarding-steps">
        <header><span>01—04</span><h3>四步完成接入</h3></header>
        {[
          ["业务服务端签发 UserSig", "AppSecret 只保存在业务服务端，客户端只接收 userID 和临时 UserSig"],
          ["初始化并登录", `使用 AppKey ${application.appKey} 初始化 ${example.label} SDK`],
          ["建立实时连接", "SDK 登录成功后复用 WebSocket；连接未建立时发送会明确失败"],
          ["同步并发送", "通过 messageId、clientMsgId 和 seq 去重排序，断线后增量补拉"],
        ].map(([title, description], index) => <div className="onboarding-step" key={title}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{title}</strong><p>{description}</p></div></div>)}
        <div className="sdk-capability-note"><Icon name="check" /><div><strong>客户端不持有 AppSecret</strong><p>{example.note}</p></div></div>
      </article>

      <article className="surface-card code-panel sdk-code-panel">
        <header><div><p className="eyebrow">{example.language.toUpperCase()}</p><h3>{example.label}</h3></div><CopyCode value={example.code} /></header>
        <div className="install-command"><span>安装</span><code>{example.install}</code><CopyCode compact value={example.install} /></div>
        <pre><code>{example.code}</code></pre>
        <div className="copy-field-grid"><CopyField label="AppKey" value={application.appKey} /><CopyField label="REST API" value={endpoints.api} /><CopyField label="WebSocket" value={endpoints.webSocket} /></div>
      </article>
    </div>
  </div>;
}

function sdkExamples(application: Application, endpoints: Endpoints): Readonly<Partial<Record<SdkPlatform, SdkExample>>> {
  const appKey = application.appKey;
  return {
    typescript: {
      label: "Web TypeScript SDK", language: "typescript", install: "npm install @sonox/im-sdk",
      note: "浏览器登录业务系统后，由业务服务端返回当前用户的 UserSig。",
      code: `import { ImClient, Message } from "@sonox/im-sdk";\n\nconst im = new ImClient({\n  appKey: "${appKey}",\n  userId: currentUser.id,\n  userSig: () => businessApi.fetchImUserSig(),\n  deviceId: installationId,\n  platform: "web",\n});\n\nawait im.login();\nawait im.connect();\nconst conversation = await im.conversationManager\n  .createPrivateConversation("user-2");\nawait im.messageManager.sendMessage(\n  conversation.convId, Message.text("你好")\n);`,
    },
    kotlin: {
      label: "Android Kotlin SDK", language: "kotlin", install: "implementation(\"com.sonox.im:android:<version>\")",
      note: "在生命周期协程中登录和连接；应用切入后台时由 SDK 管理连接恢复。",
      code: `val im = ImClient.initialize(\n  ImClientConfig(\n    appKey = "${appKey}",\n    userId = signedInUser.id,\n    userSigProvider = { businessAuth.fetchImUserSig() },\n    deviceId = installationId,\n  )\n)\n\nlifecycleScope.launch {\n  im.login()\n  im.connect()\n  val conversation = im.conversationManager\n    .createPrivateConversation("user-2")\n  im.messageManager.sendMessage(\n    conversation.convId, Message.text("你好")\n  )\n}`,
    },
    swift: {
      label: "iOS Swift SDK", language: "swift", install: ".package(url: \"https://github.com/sonox-tech/sonox-im-sdk.git\", from: \"1.0.0\")",
      note: "Swift SDK 使用 async/await 和事件流；当前离线推送 Provider 不声明 APNs 能力。",
      code: `import SonoxIM\n\nlet im = SonoxIMClient(\n  appKey: "${appKey}",\n  userID: signedInUser.id,\n  userSigProvider: { try await businessAPI.fetchIMUserSig() },\n  deviceID: installationID\n)\n\ntry await im.login()\ntry await im.connect()\nlet conversation = try await im.conversationManager\n  .createPrivateConversation(peerUserID: "user-2")\n_ = try await im.messageManager.sendMessage(\n  conversationID: conversation.convId,\n  input: IMSendInput(contentType: .text, payload: "你好")\n)`,
    },
    flutter: {
      label: "Flutter SDK", language: "dart", install: "flutter pub add sonox_im",
      note: "监听 messageBatches，并用 confirmedClientMessageIds 合并发送方回显和本地消息。",
      code: `final im = SonoxImClient(\n  appKey: '${appKey}',\n  userId: signedInUser.id,\n  userSig: businessAuth.refreshImUserSig,\n  deviceId: installationId,\n  platform: ImDevicePlatform.android,\n);\n\nawait im.login();\nawait im.connect();\nfinal recent = await im.getRecentMessages(\n  'conversation-1', count: 20,\n);`,
    },
    "java-server": {
      label: "Java Server SDK", language: "java", install: "implementation(\"com.sonox.im:server:<version>\")",
      note: `Server SDK 只调用 ${endpoints.api}，不建立客户端 WebSocket，也不包含终端登录能力。`,
      code: `try (ImServerClient im = ImServerClient.builder()\n  .appKey("${appKey}")\n  .appSecretProvider(secrets::currentImAppSecret)\n  .adminUserId("${application.adminUserId}")\n  .build()) {\n  im.accountService().importUser(\n    "user-1",\n    new AccountService.ImportUserRequest("User One", null, null, "apac-se")\n  ).get();\n}`,
    },
  };
}

function platformLabel(value: SdkPlatform): string {
  return ({ typescript: "Web / TypeScript", kotlin: "Android / Kotlin", swift: "iOS / Swift", flutter: "Flutter / Dart", "java-server": "Server / Java" })[value];
}

function CopyCode({ value, compact = false }: { readonly value: string; readonly compact?: boolean }) {
  return <Button aria-label={compact ? "复制安装命令" : "复制示例代码"} leadingIcon="copy" size="small" variant="ghost" onClick={() => { void navigator.clipboard.writeText(value); }}>{compact ? "复制" : "复制代码"}</Button>;
}
